import { ACTIONS_ROUTE, ActionsRouter } from '@kinetic-ui/actions';
import {
  CAMERA_PLAYER_PAGE_ROUTE,
  initI18n,
  reloadI18n,
  restClient,
  sessionTenantManager,
} from '@kinetic-ui/shared';
import { LoadingIndicator } from '@qfa/react-components';
import AppPreload from 'App/components/AppPreload';
import Apollo from 'App/components/AppPreload/Apollo';
import SessionCheck from 'App/components/SessionCheck';
import ShortAppPreload from 'App/components/ShortAppPreload';
import Theme from 'App/components/Theme';
import {
  BASE_ROUTE,
  COMMUNICATION_LOGS_WINDOW_ROUTE,
  NOTIFICATIONS_WINDOW_ROUTE,
} from 'constants/app';
import CameraPlayerPage from 'pages/CameraPlayerPage';
import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
/** this is an untyped shim for a regexp feature released in 2025
 * https://github.com/es-shims/RegExp.escape
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/escape
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
/** @ts-ignore */
import regexEscapeShim from 'regexp.escape/shim';
import './index.css';

import { LoadingScreen } from 'App/components/loaders';
import { AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context';
import { RecoilRoot } from 'recoil';
import { oidcConfig } from './oauth-settings';
import reportWebVitals from './reportWebVitals';
import { setupMocks } from './setupMocks';
import { getUser } from './utils/user';

regexEscapeShim();
initI18n();
setupMocks();

const appPromise = import(/* webpackChunkName: "App" */ './App');
const App = React.lazy(() => appPromise);
const NotificationWindow = React.lazy(
  () =>
    import(
      /* webpackChunkName: "NotificationWindow" */ 'pages/NotificationsPage/NotificationWindow'
    )
);

const CommunicationLogsWindow = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CommunicationLogsWindow" */ 'pages/CommunicationLogsPage/CommunicationLogsWindow'
    )
);

const ClearAuthQueryParams = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.isAuthenticated && window.location.hash?.includes('state')) {
      window.history.replaceState(
        '',
        document.title,
        window.location.pathname + window.location.search
      );
    }
  }, [auth.isAuthenticated]);
  return null;
};

const EnsureAuthenticated = () => {
  const auth = useAuth();

  useEffect(() => {
    if (auth.error) {
      if (auth.error.message === 'login_required') {
        sessionTenantManager.clearCurrentOrganization();
      }
      auth.signinRedirect();
      return;
    }

    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading) {
      auth.signinSilent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, auth.activeNavigator, auth.isLoading, auth.signinRedirect]);

  if (!auth.isAuthenticated) {
    console.warn('User is not authenticated.');
    return <LoadingScreen />;
  }
  restClient.interceptors.request.use((res) => {
    const currentUser = getUser();
    if (currentUser) {
      res.headers['Authorization'] = `Bearer ${currentUser.access_token}`;
      res.headers['x-tenant'] = sessionTenantManager.getCurrentOrganization();
    }

    return res;
  });

  return (
    <>
      <ClearAuthQueryParams />
      <RecoilRoot>
        <Apollo>
          <SessionCheck />
          <Router basename={BASE_ROUTE}>
            <Switch>
              <Route path={`/${NOTIFICATIONS_WINDOW_ROUTE}`}>
                <AppPreload>
                  <Theme>
                    <Suspense fallback={<LoadingIndicator />}>
                      <NotificationWindow />
                    </Suspense>
                  </Theme>
                </AppPreload>
              </Route>
              <Route path={`/${CAMERA_PLAYER_PAGE_ROUTE}/:id`}>
                <ShortAppPreload>
                  <Theme>
                    <CameraPlayerPage />
                  </Theme>
                </ShortAppPreload>
              </Route>
              <Route path={`/${COMMUNICATION_LOGS_WINDOW_ROUTE}`}>
                <AppPreload>
                  <Theme>
                    <Suspense fallback={<LoadingIndicator />}>
                      <CommunicationLogsWindow />
                    </Suspense>
                  </Theme>
                </AppPreload>
              </Route>
              <Route path={`/${ACTIONS_ROUTE}`}>
                <ShortAppPreload>
                  <Theme>
                    <ActionsRouter />
                  </Theme>
                </ShortAppPreload>
              </Route>
              <Route path='*'>
                <AppPreload>
                  <App />
                </AppPreload>
              </Route>
            </Switch>
          </Router>
        </Apollo>
      </RecoilRoot>
    </>
  );
};

ReactDOM.render(
  <React.StrictMode>
    <AuthProvider {...oidcConfig}>
      <EnsureAuthenticated />
    </AuthProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

/**
 * listen to window messages for hot-reload
 */
window.addEventListener('message', (e) => {
  const event = e.data.payload?.event;
  // if (event !== 'operations') {
  //   console.log({ event });
  // }
  if (event === 'fastRefreshScheduled') {
    // reload i18n files
    reloadI18n();
  }
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
